import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Badge } from "../../base/badge/badge";
import { Input } from "../../base/input/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../base/select/select";
import { cn } from "../../../utils";
import { DataTableDateRangeFilter } from "./filters/DataTableDateRangeFilter";
import { serializeNumberRange, deserializeNumberRange } from "./filters/utils";
/**
 * Filter chip component that appears in the DataTable toolbar.
 * Shows column name and an input/select for filtering.
 * Auto-focuses when created for immediate user input.
 *
 * @example
 * ```tsx
 * <DataTableFilterChip
 *   column={column}
 *   columnLabel="Name"
 *   filterValue={search.name_filter || ''}
 *   onUpdate={(value) => handleUpdateFilter('name', value)}
 *   onRemove={() => handleRemoveFilter('name')}
 *   autoFocus={true}
 * />
 * ```
 */ export function DataTableFilterChip({ column, columnLabel, filterValue, onUpdate, onRemove, autoFocus = false }) {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const [localValue, setLocalValue] = useState(filterValue);
    const filterType = column.columnDef.meta?.filterType || "text";
    const filterOptions = column.columnDef.meta?.filterOptions;
    // For number range filters, maintain separate min/max state
    const deserializedRange = filterType === "numberRange" ? deserializeNumberRange(filterValue) : {
        min: "",
        max: ""
    };
    const [localMin, setLocalMin] = useState(deserializedRange.min);
    const [localMax, setLocalMax] = useState(deserializedRange.max);
    useEffect(function focusInputOnCreate() {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [
        autoFocus
    ]);
    useEffect(function syncLocalValueWithFilter() {
        setLocalValue(filterValue);
    }, [
        filterValue
    ]);
    useEffect(function syncNumberRangeWithFilter() {
        if (filterType === "numberRange") {
            const range = deserializeNumberRange(filterValue);
            setLocalMin(range.min);
            setLocalMax(range.max);
        }
    }, [
        filterValue,
        filterType
    ]);
    useEffect(function debounceTextFilterUpdate() {
        if (filterType === "text") {
            const timer = setTimeout(()=>{
                onUpdate(localValue);
            }, 300);
            return ()=>clearTimeout(timer);
        }
    }, [
        localValue,
        filterType,
        onUpdate
    ]);
    useEffect(function debounceNumberRangeFilterUpdate() {
        if (filterType === "numberRange") {
            const timer = setTimeout(()=>{
                const serialized = serializeNumberRange(localMin, localMax);
                onUpdate(serialized);
            }, 300);
            return ()=>clearTimeout(timer);
        }
    }, [
        localMin,
        localMax,
        filterType,
        onUpdate
    ]);
    const renderFilterInput = ()=>{
        switch(filterType){
            case "select":
                if (!filterOptions || filterOptions.length === 0) {
                    return /*#__PURE__*/ _jsx(Input, {
                        ref: inputRef,
                        type: "text",
                        value: localValue,
                        onChange: (e)=>setLocalValue(e.target.value),
                        className: "h-6 w-[120px] text-2xs px-2 text-foreground font-normal",
                        placeholder: t("dataTable.filterValuePlaceholder", {
                            defaultValue: "Enter {{label}}...",
                            label: columnLabel
                        })
                    });
                }
                return /*#__PURE__*/ _jsxs(Select, {
                    value: localValue,
                    onValueChange: onUpdate,
                    children: [
                        /*#__PURE__*/ _jsx(SelectTrigger, {
                            className: "h-6 w-[120px] text-2xs px-2 border-none shadow-none text-foreground font-normal",
                            children: /*#__PURE__*/ _jsx(SelectValue, {
                                placeholder: t("dataTable.selectPlaceholder", {
                                    defaultValue: "Select..."
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx(SelectContent, {
                            children: filterOptions.map((option)=>/*#__PURE__*/ _jsx(SelectItem, {
                                    value: option.value,
                                    children: option.label
                                }, option.value))
                        })
                    ]
                });
            case "numberRange":
                return /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-1.5",
                    children: [
                        /*#__PURE__*/ _jsx(Input, {
                            ref: inputRef,
                            type: "number",
                            value: localMin,
                            onChange: (e)=>setLocalMin(e.target.value),
                            className: "h-6 w-[70px] text-2xs px-2 text-foreground font-normal",
                            placeholder: t("dataTable.filters.min", {
                                defaultValue: "Min"
                            })
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "text-xs text-foreground",
                            children: "-"
                        }),
                        /*#__PURE__*/ _jsx(Input, {
                            type: "number",
                            value: localMax,
                            onChange: (e)=>setLocalMax(e.target.value),
                            className: "h-6 w-[70px] text-2xs px-2 text-foreground font-normal",
                            placeholder: t("dataTable.filters.max", {
                                defaultValue: "Max"
                            })
                        })
                    ]
                });
            case "dateRange":
                return /*#__PURE__*/ _jsx("div", {
                    className: "scale-90 origin-left",
                    children: /*#__PURE__*/ _jsx(DataTableDateRangeFilter, {
                        column: column
                    })
                });
            case "text":
            default:
                return /*#__PURE__*/ _jsx(Input, {
                    ref: inputRef,
                    type: "text",
                    value: localValue,
                    onChange: (e)=>setLocalValue(e.target.value),
                    className: "h-6 w-[120px] text-2xs px-2 text-foreground font-normal",
                    placeholder: t("dataTable.filterValuePlaceholder", {
                        defaultValue: "Enter {{label}}...",
                        label: columnLabel
                    })
                });
        }
    };
    return /*#__PURE__*/ _jsxs(Badge, {
        className: cn("flex items-center gap-2 px-2 py-1 rounded-md", "bg-muted border-line-strong text-foreground"),
        children: [
            /*#__PURE__*/ _jsxs("span", {
                className: "text-xs font-medium text-fg-secondary",
                children: [
                    columnLabel,
                    ":"
                ]
            }),
            renderFilterInput(),
            /*#__PURE__*/ _jsx("button", {
                onClick: onRemove,
                className: "rounded-xs hover:bg-surface-strong p-0.5 transition-colors",
                "aria-label": `Remove ${columnLabel} filter`,
                children: /*#__PURE__*/ _jsx(X, {
                    className: "h-3 w-3 text-foreground"
                })
            })
        ]
    });
}

//# sourceMappingURL=DataTableFilterChip.js.map